import { useRef } from "react";
import {
  Avatar,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Text,
  VStack,
  Flex,
  Input,
} from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import { getIconComponent } from "src/Manager/FeedManager/SocialInput";
import { FaTrash } from "react-icons/fa";
import type { CreativeBlock } from "../guidedFlow/types";
import type { FacebookPage, InstagramAccount } from "../types";
import {
  creativeWithResolvedInstagramRowOnly,
  creativeWithSyncedInstagram,
} from "../utils/creativesWithInstagramFromFetch";
import WhiteBgInput from "../WhiteBgInput";
import { PRIMARY_TEXT_MAX } from "../forms/campaignFlowForm";
import MediaUploadArea from "./MediaUploadArea";

export type CreativeBlockEditorProps = {
  creative: CreativeBlock;
  index: number;
  totalCreatives: number;
  pageOptions: { label: string; value: FacebookPage }[];
  instagramOptions: { label: string; value: InstagramAccount }[];
  createUploadUrlForSlot: (
    slotKey: string
  ) => (opts: {
    file: File;
    callback: (r: { signedUrl: string }) => void | Promise<string | void>;
  }) => Promise<{ signedUrl: string; fileKey: string }>;
  uploadRefs: React.MutableRefObject<
    Record<string, { fileKey?: string; cdn_url?: string }>
  >;
  onUpdate: (index: number, patch: Partial<CreativeBlock>) => void;
  onRemove: (index: number) => void;
  onAddMore: () => void;
  /**
   * When the user picks a Facebook page, fetch Instagram actors for that page.
   * Return rows so the editor can merge: by default only canonicalizes an already
   * selected id; see {@link applyDefaultInstagramOnFacebookPageChange}.
   */
  onFacebookPageChange?: (
    facebookPageId: string
  ) => void | Promise<InstagramAccount[] | void>;
  /**
   * When true (guided create), after a successful fetch the first Instagram account
   * is selected if the row had none. Edit flows keep Instagram empty until the user picks.
   */
  applyDefaultInstagramOnFacebookPageChange?: boolean;
  editingName: { index: number; value: string } | null;
  onEditingNameChange: (v: { index: number; value: string } | null) => void;
  allowCreativeCountChanges?: boolean;
  isDisabled?: boolean;
  /** When true, the Facebook Page select is disabled and shows a loading state. */
  isFacebookPagesLoading?: boolean;
  /** When true, the Instagram select is disabled and shows a loading state (e.g. after page change). */
  isInstagramAccountsLoading?: boolean;
  /** When true, the Instagram select cannot be cleared (clear control hidden). */
  instagramAccountClearDisabled?: boolean;
  selectContainerStyles: Record<
    string,
    (p: Record<string, unknown>) => Record<string, unknown>
  >;
};

export default function CreativeBlockEditor({
  creative,
  index,
  totalCreatives,
  pageOptions,
  instagramOptions,
  createUploadUrlForSlot,
  uploadRefs,
  onUpdate,
  onRemove,
  onAddMore,
  onFacebookPageChange,
  applyDefaultInstagramOnFacebookPageChange = false,
  editingName,
  onEditingNameChange,
  allowCreativeCountChanges = true,
  isDisabled = false,
  isFacebookPagesLoading = false,
  isInstagramAccountsLoading = false,
  instagramAccountClearDisabled = false,
  selectContainerStyles,
}: CreativeBlockEditorProps) {
  const facebookPageChangeSeq = useRef(0);
  const instagramClearDisabled = instagramAccountClearDisabled;
  const slotKeySquare = `${index}-square`;
  const slotKeyVertical = `${index}-vertical`;
  const isEditingName = editingName?.index === index;
  const hasSquare = Boolean(
    (creative.squareMediaUrl || creative.squareMediaFileKey) &&
      creative.squareMediaType
  );
  const hasVertical = Boolean(
    (creative.verticalMediaUrl || creative.verticalMediaFileKey) &&
      creative.verticalMediaType
  );

  return (
    <Box
      backgroundColor="white"
      rounded="lg"
      p={4}
      border="1px solid"
      borderColor="gray.200"
      pb={8}
    >
      <HStack
        justify="space-between"
        align="center"
        w="full"
        mb={2}
        flexWrap="wrap"
        gap={2}
      >
        {isEditingName ? (
          <HStack align="center" spacing={2} flex={1} minW="200px">
            <Input
              size="sm"
              value={editingName.value}
              isDisabled={isDisabled}
              onChange={(e) =>
                onEditingNameChange(
                  editingName ? { ...editingName, value: e.target.value } : null
                )
              }
              onKeyDown={(e) => {
                if (isDisabled) {
                  return;
                }
                if (e.key === "Enter") {
                  onUpdate(index, {
                    name: editingName.value.trim() || undefined,
                  });
                  onEditingNameChange(null);
                }
                if (e.key === "Escape") {
                  onEditingNameChange(null);
                }
              }}
              autoFocus
              placeholder={`Ad ${index + 1}`}
            />
            <Button
              size="sm"
              bgColor="gray.800"
              color="gray.50"
              px={4}
              isDisabled={isDisabled}
              _hover={{ bgColor: "blue.800" }}
              onClick={() => {
                onUpdate(index, {
                  name: editingName.value.trim() || undefined,
                });
                onEditingNameChange(null);
              }}
            >
              Save
            </Button>
            <Button
              px={4}
              size="sm"
              variant="ghost"
              isDisabled={isDisabled}
              onClick={() => onEditingNameChange(null)}
            >
              Cancel
            </Button>
          </HStack>
        ) : (
          <>
            <HStack align="center" spacing={2} flexWrap="wrap" px={2}>
              <Text fontSize="sm" fontWeight="semibold">
                {creative.name || `Ad ${index + 1}`}
              </Text>
              <Button
                size="xs"
                variant="ghost"
                colorScheme="blue"
                isDisabled={isDisabled}
                onClick={() =>
                  onEditingNameChange({
                    index,
                    value: creative.name || `Ad ${index + 1}`,
                  })
                }
              >
                Rename
              </Button>
            </HStack>
            <Text
              fontSize="xs"
              color={
                hasSquare && hasVertical
                  ? "green.600"
                  : hasSquare || hasVertical
                    ? "green.600"
                    : "gray.500"
              }
              data-testid={`meta-ads-creative-formats-status-${index}`}
            >
              {hasSquare && hasVertical
                ? "Both formats added"
                : !hasSquare && !hasVertical
                  ? "Add at least one format (square 1:1 or vertical 9:16)"
                  : !hasSquare
                    ? "Square optional (1:1)"
                    : "Vertical optional (9:16)"}
            </Text>
          </>
        )}
      </HStack>

      <HStack align="flex-start" spacing={4} flexWrap="wrap" w="100%">
        <Box flex="1">
          <Box
            aspectRatio={"1"}
            border="1px dashed"
            borderColor="rgba(0, 0, 0, 0.3)"
            rounded="lg"
            bg="gray.50"
            _hover={{ borderColor: "gray.400" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <MediaUploadArea
              variant="modern"
              ratio="1/1"
              name="Square"
              fileInputTestId={`meta-ads-creative-upload-${index}-square`}
              noPadding={true}
              mediaType={
                creative.squareMediaType === "video" ? "video" : "photo"
              }
              url={creative.squareMediaUrl}
              createUploadUrl={createUploadUrlForSlot(slotKeySquare)}
              onChange={(result) => {
                if (!result.mime_type?.trim()) {
                  onUpdate(index, {
                    squareMediaUrl: undefined,
                    squareMediaFileKey: undefined,
                    squareMediaType: undefined,
                  });
                  return;
                }
                onUpdate(index, {
                  squareMediaUrl:
                    result.cdn_url ??
                    uploadRefs.current[slotKeySquare]?.cdn_url,
                  squareMediaFileKey:
                    result.file_key ??
                    uploadRefs.current[slotKeySquare]?.fileKey,
                  squareMediaType: result.mime_type.startsWith("video/")
                    ? "video"
                    : "image",
                });
              }}
              isDisabled={isDisabled}
              p={4}
            />
          </Box>
        </Box>
        <Box flex="1">
          <Box
            aspectRatio={"1"}
            border="1px dashed"
            borderColor="rgba(0, 0, 0, 0.3)"
            rounded="lg"
            bg="gray.50"
            _hover={{ borderColor: "gray.400" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <MediaUploadArea
              variant="modern"
              ratio="9/16"
              name="Vertical"
              fileInputTestId={`meta-ads-creative-upload-${index}-vertical`}
              noPadding={true}
              mediaType={
                creative.verticalMediaType === "video" ? "video" : "photo"
              }
              url={creative.verticalMediaUrl}
              createUploadUrl={createUploadUrlForSlot(slotKeyVertical)}
              onChange={(result) => {
                if (!result.mime_type?.trim()) {
                  onUpdate(index, {
                    verticalMediaUrl: undefined,
                    verticalMediaFileKey: undefined,
                    verticalMediaType: undefined,
                  });
                  return;
                }
                onUpdate(index, {
                  verticalMediaUrl:
                    result.cdn_url ??
                    uploadRefs.current[slotKeyVertical]?.cdn_url,
                  verticalMediaFileKey:
                    result.file_key ??
                    uploadRefs.current[slotKeyVertical]?.fileKey,
                  verticalMediaType: result.mime_type.startsWith("video/")
                    ? "video"
                    : "image",
                });
              }}
              isDisabled={isDisabled}
              p={4}
            />
          </Box>
        </Box>
      </HStack>

      <FormControl mt={4}>
        <Flex justify="space-between" align="center" mb={1}>
          <FormLabel fontSize="sm" fontWeight="semibold" mb={0}>
            Primary text
          </FormLabel>
          <Text fontSize="xs" color="gray.500">
            {creative.primaryText.length}/{PRIMARY_TEXT_MAX} Characters
          </Text>
        </Flex>
        <WhiteBgInput
          as="textarea"
          resize="vertical"
          maxLength={PRIMARY_TEXT_MAX}
          isDisabled={isDisabled}
          value={creative.primaryText}
          onChange={(e) =>
            onUpdate(index, {
              primaryText: e.target.value.slice(0, PRIMARY_TEXT_MAX),
            })
          }
          placeholder="Primary text here"
          style={{ paddingTop: "5px", display: "flex", minHeight: "54px" }}
        />
      </FormControl>

      <HStack spacing={4} mt={4} align="flex-start" flexWrap="wrap">
        <FormControl flex={1} minW="160px">
          <FormLabel fontSize="sm" fontWeight="semibold">
            Facebook Page
          </FormLabel>
          <Select
            options={pageOptions}
            isDisabled={isDisabled || isFacebookPagesLoading}
            isLoading={isFacebookPagesLoading}
            value={
              creative.fbPage
                ? (pageOptions.find(
                    (o) =>
                      o.value.id ===
                      (typeof creative.fbPage === "object"
                        ? creative.fbPage?.id
                        : creative.fbPage)
                  ) ?? null)
                : null
            }
            onChange={(opt) => {
              const page = opt?.value ?? null;
              const pageId = page?.id;
              const pagePatch = {
                fbPage: page,
                ...(instagramClearDisabled ? {} : { instagramAccount: null }),
              } as const;
              const afterPage: CreativeBlock = { ...creative, ...pagePatch };
              onUpdate(index, pagePatch);
              if (!pageId || !onFacebookPageChange) return;
              const seq = ++facebookPageChangeSeq.current;
              Promise.resolve(onFacebookPageChange(pageId)).then((accounts) => {
                if (facebookPageChangeSeq.current !== seq) return;
                if (instagramClearDisabled) return;
                const list = Array.isArray(accounts) ? accounts : [];
                const synced = applyDefaultInstagramOnFacebookPageChange
                  ? creativeWithSyncedInstagram(afterPage, list)
                  : creativeWithResolvedInstagramRowOnly(afterPage, list);
                const nextIg = synced.instagramAccount;
                const prevIg = afterPage.instagramAccount;
                if (
                  (nextIg?.id ?? "") !== (prevIg?.id ?? "") ||
                  (nextIg?.username ?? "") !== (prevIg?.username ?? "") ||
                  (nextIg?.name ?? "") !== (prevIg?.name ?? "")
                ) {
                  onUpdate(index, {
                    instagramAccount: nextIg ?? null,
                  });
                }
              });
            }}
            placeholder={
              isFacebookPagesLoading ? "Loading pages…" : "Select page"
            }
            chakraStyles={selectContainerStyles}
            formatOptionLabel={(option) => (
              <HStack spacing={2}>
                <Avatar
                  size="xs"
                  name={option.value.name}
                  src={option.value.picture}
                />
                <Text fontSize="sm" noOfLines={1}>
                  {option.value.name}
                </Text>
              </HStack>
            )}
          />
        </FormControl>
        <FormControl flex={1} minW="160px">
          <FormLabel fontSize="sm" fontWeight="semibold">
            Instagram Account
          </FormLabel>
          <Select
            options={instagramOptions}
            isDisabled={isDisabled || isInstagramAccountsLoading}
            isLoading={isInstagramAccountsLoading}
            isClearable={!instagramClearDisabled && !isInstagramAccountsLoading}
            value={
              creative.instagramAccount
                ? (instagramOptions.find(
                    (o) =>
                      o.value.id ===
                      (typeof creative.instagramAccount === "object"
                        ? creative.instagramAccount?.id
                        : creative.instagramAccount)
                  ) ?? null)
                : null
            }
            onChange={(opt) => {
              if (instagramClearDisabled && !opt?.value) return;
              onUpdate(index, { instagramAccount: opt?.value ?? null });
            }}
            placeholder={
              isInstagramAccountsLoading
                ? "Loading accounts…"
                : "Select Instagram"
            }
            chakraStyles={selectContainerStyles}
            formatOptionLabel={(option) => (
              <HStack spacing={2}>
                <Avatar
                  size="xs"
                  name={option.value.username}
                  src={option.value.profile_picture_url}
                />
                <Text fontSize="sm" noOfLines={1}>
                  {option.value.username
                    ? `@${option.value.username}`
                    : option.value.name}
                </Text>
              </HStack>
            )}
          />
        </FormControl>
      </HStack>

      <Box mx="auto" width="full" height="0" textAlign="center" mt={4} mb={-4}>
        {allowCreativeCountChanges && index === totalCreatives - 1 && (
          <Button
            display="inline-block"
            size="sm"
            fontWeight="medium"
            variant="outline"
            rightIcon={<Text>+</Text>}
            bgColor="gray.800"
            color="white"
            width="fit-content"
            onClick={onAddMore}
          >
            Add More
          </Button>
        )}
        {allowCreativeCountChanges && totalCreatives > 1 && index !== 0 && (
          <Button
            ml={2}
            size="sm"
            variant="outline"
            bg="red.50"
            colorScheme="red"
            onClick={() => onRemove(index)}
          >
            <FaTrash />
          </Button>
        )}
      </Box>
    </Box>
  );
}
